/**
 * OnboardingFlow — the post-tutorial mascot-led questionnaire.
 * Internal step machine: intro1 → intro2 → age → name → leagues → done (age skipped once verified 18+). Presentational: the route
 * injects the username/leagues data + callbacks and wires navigation (onFinish → first pick).
 *
 * See specs/onboarding-questionnaire/spec.md.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Animated, Easing } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
  nextStep,
  prevStep,
  questionnaireProgress,
  canAdvance,
  type QuestionnaireStep,
} from '../../../src/onboarding/helpers/onboardingFlowStep.helper';
import { validateUsernameCandidate } from '../../../src/onboarding/helpers/validateUsernameCandidate.helper';
import type { UsernameAvailability, SetUsernameResult } from '../../../src/onboarding/api/usernameApi';
import type { UseAgeVerificationResult } from '../../../src/ageGate/hooks/useAgeVerification';
import { onboardingStyles as s, C } from './OnboardingFlow.styles';
import { ONBOARDING_COPY, ONBOARDING_TEST_IDS } from './OnboardingFlow.constants';
import { IntroStep } from './steps/IntroStep';
import { AgeStep } from './steps/AgeStep';
import { UsernameStep } from './steps/UsernameStep';
import { LeaguesStep, type OnboardingLeague } from './steps/LeaguesStep';
import { DoneStep } from './steps/DoneStep';
import { OnboardingHeroBackground } from '../OnboardingHeroBackground/OnboardingHeroBackground';

type CheckStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid' | 'profanity' | 'error';

export interface OnboardingFlowProps {
  checkUsername: (name: string) => Promise<UsernameAvailability>;
  saveUsername: (name: string) => Promise<SetUsernameResult>;
  /** 18+ age-gate state + actions for the age step (the route passes `useAgeVerification()`). */
  age: Pick<UseAgeVerificationResult, 'status' | 'isSubmitting' | 'submitError' | 'submit' | 'signOut'>;
  leagues: OnboardingLeague[];
  leaguesLoading: boolean;
  selectedLeagueIds: Set<string>;
  onToggleLeague: (id: string) => void;
  /** Finish → first Monkey pick (Create). */
  onFinish: () => void;
  /** Back out of the very first step (→ tutorial/welcome entry). */
  onExit: () => void;
  /** Starting step (defaults to intro1) — used by stories/tests to jump to a step. */
  initialStep?: QuestionnaireStep;
}

function statusDisplay(status: CheckStatus, name: string): { text: string; color: string } | null {
  switch (status) {
    case 'checking': return { text: ONBOARDING_COPY.statusChecking, color: C.muted };
    case 'available': return { text: ONBOARDING_COPY.statusAvailable(name), color: C.good };
    case 'taken': return { text: ONBOARDING_COPY.statusTaken, color: C.bad };
    case 'invalid': return { text: ONBOARDING_COPY.statusInvalid, color: C.bad };
    case 'profanity': return { text: ONBOARDING_COPY.statusProfanity, color: C.bad };
    case 'error': return { text: ONBOARDING_COPY.statusError, color: C.bad };
    default: return null;
  }
}

export function OnboardingFlow({
  checkUsername, saveUsername, age, leagues, leaguesLoading,
  selectedLeagueIds, onToggleLeague, onFinish, onExit, initialStep = 'intro1',
}: OnboardingFlowProps) {
  const [step, setStep] = useState<QuestionnaireStep>(initialStep);
  // The frame currently on screen. It lags `step` by one out-animation so the old frame can
  // fade/settle out before the new one fades in (see the transition effect below).
  const [displayedStep, setDisplayedStep] = useState<QuestionnaireStep>(initialStep);
  const [transitioning, setTransitioning] = useState(false);
  const [username, setUsername] = useState('');
  const [checkStatus, setCheckStatus] = useState<CheckStatus>('idle');
  const [checkedName, setCheckedName] = useState('');
  const [saving, setSaving] = useState(false);

  const ageVerified = age.status === 'verified';

  const goNext = useCallback(() => {
    const n = nextStep(step, { ageVerified });
    if (n) setStep(n);
    else onFinish();
  }, [step, ageVerified, onFinish]);

  const goBack = useCallback(() => {
    const p = prevStep(step, { ageVerified });
    if (p) setStep(p);
    else onExit();
  }, [step, ageVerified, onExit]);

  // A successful submit (or a status check that finds a date already on file) moves the
  // user on to the username step without a second tap (AC-19).
  useEffect(() => {
    if (step === 'age' && ageVerified) setStep('name');
  }, [step, ageVerified]);

  const onChangeUsername = useCallback((t: string) => {
    setUsername(t);
    setCheckStatus('idle'); // editing invalidates a prior check → Continue re-gates
  }, []);

  const runCheck = useCallback(async () => {
    const local = validateUsernameCandidate(username);
    if (!local.valid) {
      setCheckStatus(local.reason === 'profanity' ? 'profanity' : 'invalid');
      return;
    }
    setCheckStatus('checking');
    const result: UsernameAvailability = await checkUsername(local.normalized);
    if (result.available) {
      setCheckedName(local.normalized);
      setCheckStatus('available');
    } else {
      setCheckStatus(result.reason === 'taken' ? 'taken'
        : result.reason === 'profanity' ? 'profanity'
          : result.reason === 'invalid' ? 'invalid' : 'error');
    }
  }, [username, checkUsername]);

  const confirmUsername = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    const result = await saveUsername(checkedName || username.trim());
    setSaving(false);
    if (result.ok) {
      goNext();
    } else if (result.reason === 'taken') {
      setCheckStatus('taken'); // race: taken between check and save
    } else {
      setCheckStatus(result.reason === 'invalid' ? 'invalid' : 'error');
    }
  }, [saving, saveUsername, checkedName, username, goNext]);

  const usernameAvailable = checkStatus === 'available' && username.trim() === checkedName;
  const progress = questionnaireProgress(step);

  // Between-frame transition: the outgoing frame fades + settles down, then the incoming frame
  // is swapped in and fades + lifts into place — a clean cross-dissolve instead of a hard cut.
  // Built-in Animated (like AnimatedMonkey) so it runs on native, web, and Storybook. The initial
  // mount is skipped so the first frame (and story/test render) appears instantly.
  const frameAnim = useRef(new Animated.Value(1)).current;
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (step === displayedStep) return;
    setTransitioning(true);
    frameAnim.stopAnimation(() => {
      Animated.timing(frameAnim, {
        toValue: 0,
        duration: 140,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }).start(({ finished }) => {
        if (!finished) return;
        setDisplayedStep(step); // swap content while invisible
        Animated.timing(frameAnim, {
          toValue: 1,
          duration: 240,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }).start(() => setTransitioning(false));
      });
    });
  }, [step, displayedStep, frameAnim]);
  const frameStyle = useMemo(
    () => ({
      flex: 1,
      opacity: frameAnim,
      transform: [
        { translateY: frameAnim.interpolate({ inputRange: [0, 1], outputRange: [10, 0] }) },
        { scale: frameAnim.interpolate({ inputRange: [0, 1], outputRange: [0.98, 1] }) },
      ],
    }),
    [frameAnim],
  );

  const body = useMemo(() => {
    switch (displayedStep) {
      case 'intro1':
        return (
          <IntroStep
            text={ONBOARDING_COPY.intro1Text}
            ctaLabel={ONBOARDING_COPY.intro1Cta}
            onNext={goNext}
            onBack={goBack}
          />
        );
      case 'intro2':
        return (
          <IntroStep
            text={ONBOARDING_COPY.intro2Text}
            ctaLabel={ONBOARDING_COPY.intro2Cta}
            onNext={goNext}
            onBack={goBack}
          />
        );
      case 'age':
        return (
          <AgeStep
            blocked={age.status === 'blocked'}
            isSubmitting={age.isSubmitting}
            submitError={age.submitError}
            onSubmit={(parts) => void age.submit(parts)}
            onSignOut={() => void age.signOut()}
            onBack={goBack}
            progress={progress}
          />
        );
      case 'name':
        return (
          <UsernameStep
            value={username}
            onChangeText={onChangeUsername}
            onCheck={runCheck}
            onContinue={confirmUsername}
            onBack={goBack}
            checkLabel={checkStatus === 'checking' ? ONBOARDING_COPY.checkChecking
              : usernameAvailable ? ONBOARDING_COPY.checkFree : ONBOARDING_COPY.checkIdle}
            checkDisabled={checkStatus === 'checking' || username.trim().length === 0}
            checkAvailable={usernameAvailable}
            status={statusDisplay(checkStatus, username.trim())}
            continueEnabled={canAdvance('name', { usernameAvailable }) && !saving}
            progress={progress}
          />
        );
      case 'leagues':
        return (
          <LeaguesStep
            leagues={leagues}
            loading={leaguesLoading}
            selectedIds={selectedLeagueIds}
            onToggle={onToggleLeague}
            onContinue={goNext}
            onBack={goBack}
            progress={progress}
          />
        );
      case 'done':
        return (
          <DoneStep
            name={checkedName || username.trim() || 'friend'}
            followingCount={selectedLeagueIds.size}
            onStart={goNext}
          />
        );
      default:
        return null;
    }
  }, [
    displayedStep, age, username, onChangeUsername, runCheck, confirmUsername, goNext, goBack,
    checkStatus, usernameAvailable, saving, progress, leagues, leaguesLoading,
    selectedLeagueIds, onToggleLeague, checkedName,
  ]);

  return (
    <View style={s.container} testID={ONBOARDING_TEST_IDS.CONTAINER}>
      <OnboardingHeroBackground />
      <SafeAreaView style={s.safe} edges={['top', 'bottom', 'left', 'right']}>
        <Animated.View style={frameStyle} pointerEvents={transitioning ? 'none' : 'auto'}>
          {body}
        </Animated.View>
      </SafeAreaView>
    </View>
  );
}
